// V2 Configuration for Drug Selection
// This file contains the configuration for primary and secondary drug selection

export interface DrugOption {
  label: string;
  value: string;
}

export interface PrimaryDrugOption extends DrugOption {
  cancerTypes?: CancerTypeV2[];
}

export interface CancerTypeV2 {
  label: string;
  value: string;
  therapies: TherapyOptionV2[];
}

export interface TherapyOptionV2 {
  label: string;
  value: string;
  indications: IndicationOption[];
}

export interface IndicationOption {
  label: string;
  value: string;
}

export interface SecondaryDrugOption extends DrugOption {
  // Secondary drugs are simple options without nested data
}

export interface SecondaryDrugWithStatus {
  drug_name: string;
  is_free: 0 | 1; // 0 = Paid, 1 = Free
}

export interface FormPayloadV2 {
  // Primary Drug Fields
  primaryDrug?: string;
  primaryDrugPaidStatus?: 0 | 1; // 0 = Paid (default), 1 = Free
  
  // Secondary Drug Fields - Now each drug has its own status
  secondaryDrugs?: SecondaryDrugWithStatus[]; // Array of drugs with individual status
  
  // Cancer and Therapy Details
  cancerType?: string;
  therapy?: string;
  indication?: string;
  
  // Cycle/Session Details
  cycle_number?: string;
  cycles_completed?: number;
  upcoming_cycle_date?: string;
  upcoming_dosage?: string;
  notes?: string;
  brain_mets?: 0 | 1;
  
  // Rewards
  isRewardsAssign?: boolean;
}

// Primary Drug Options (Single Select Dropdown)
export const primaryDrugOptions: PrimaryDrugOption[] = [
  {
    label: "Sigrima",
    value: "sigrima",
  },
  {
    label: "Nivolumab",
    value: "nivolumab",
  },
  {
    label: "Tucatinib",
    value: "tucatinib",
  },
];

// Secondary Drug Options (Multi-Select Dropdown)
// This represents drugs like VIVITRA
export const secondaryDrugOptions: SecondaryDrugOption[] = [
  {
    label: "VIVITRA",
    value: "vivitra",
  },
  {
    label: "Trastuzumab",
    value: "trastuzumab",
  },
  {
    label: "Capecitabine",
    value: "capecitabine",
  },
  {
    label: "Ipilimumab",
    value: "ipilimumab",
  },
  {
    label: "Cabozantinib",
    value: "cabozantinib",
  },
];

// Paid/Free Status Options
export const paidFreeOptions: DrugOption[] = [
  { label: "Paid Drug", value: "0" },
  { label: "Free Drug", value: "1" },
];

// Cancer Types and Therapies for each Primary Drug
export const primaryDrugCancerTypesV2: Record<string, CancerTypeV2[]> = {
  nivolumab: [
    {
      label: "NSCLC – Non-Small Cell Lung Cancer",
      value: "NSCLC – Non-Small Cell Lung Cancer",
      therapies: [
        {
          label: "Neo - Adjuvant",
          value: "Neo - Adjuvant",
          indications: [
            {
              label: "In Combination with platinum doublet chemotherapy (Pemetrexed+Carbo/Pacli+Carbo)",
              value: "In Combination with platinum doublet chemotherapy (Pemetrexed+Carbo/Pacli+Carbo)",
            },
          ],
        },
        {
          label: "1L",
          value: "1L",
          indications: [
            {
              label: "In Combination with Ipilimumab + Platinum Chemo (Pem+Cis/Carbo)",
              value: "In Combination with Ipilimumab + Platinum Chemo (Pem+Cis/Carbo)",
            },
            {
              label: "Chemo free regimen​",
              value: "Chemo free regimen​",
            },
          ],
        },
        {
          label: "2L",
          value: "2L",
          indications: [
            {
              label: "Single agent​",
              value: "Single agent​",
            },
          ],
        },
      ],
    },
    {
      label: "GI – Gastrointestinal",
      value: "GI – Gastrointestinal",
      therapies: [
        {
          label: "1L Gastric",
          value: "1L Gastric",
          indications: [
            {
              label: "In combination with Fluoropyrimidine & platinum (Cisplatin) containing chemo",
              value: "In combination with Fluoropyrimidine & platinum (Cisplatin) containing chemo",
            },
          ],
        },
        {
          label: "GEJC",
          value: "GEJC",
          indications: [
            {
              label: "In combination with Fluoropyrimidine & platinum (Cisplatin) containing chemo",
              value: "In combination with Fluoropyrimidine & platinum (Cisplatin) containing chemo",
            },
          ],
        },
        {
          label: "EAC",
          value: "EAC",
          indications: [
            {
              label: "In combination with Fluoropyrimidine & platinum (Cisplatin) containing chemo",
              value: "In combination with Fluoropyrimidine & platinum (Cisplatin) containing chemo",
            },
          ],
        },
        {
          label: "1L ESCC",
          value: "1L ESCC",
          indications: [
            {
              label: "In combination with Fluoropyrimidine & platinum (Cisplatin) containing chemo",
              value: "In combination with Fluoropyrimidine & platinum (Cisplatin) containing chemo",
            },
          ],
        },
        {
          label: "2L ESCC",
          value: "2L ESCC",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
        {
          label: "ADJ. Eso",
          value: "ADJ. Eso",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
      ],
    },
    {
      label: "RCC – Renal Cell Carcinoma",
      value: "RCC – Renal Cell Carcinoma",
      therapies: [
        {
          label: "1L",
          value: "1L",
          indications: [
            {
              label: "In combination with Cabozantinib",
              value: "In combination with Cabozantinib",
            },
            {
              label: "In Combination with Ipilimumab",
              value: "In Combination with Ipilimumab",
            },
          ],
        },
        {
          label: "2L",
          value: "2L",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
      ],
    },
    {
      label: "SCCHN – Squamous Cell Carcinoma of the Head and Neck",
      value: "SCCHN – Squamous Cell Carcinoma of the Head and Neck",
      therapies: [
        {
          label: "2L",
          value: "2L",
          indications: [
            {
              label: "Nivolumab is approved in patients with SCCHN after platinum-based therapy",
              value: "Nivolumab is approved in patients with SCCHN after platinum-based therapy",
            },
          ],
        },
      ],
    },
    {
      label: "Urothelial Carcinoma",
      value: "Urothelial Carcinoma",
      therapies: [
        {
          label: "Adjuvant",
          value: "Adjuvant",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
        {
          label: "2L",
          value: "2L",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
      ],
    },
    {
      label: "Melanoma",
      value: "Melanoma",
      therapies: [
        {
          label: "Adjuvant",
          value: "Adjuvant",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
        {
          label: "Metastatic",
          value: "Metastatic",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
      ],
    },
    {
      label: "CHL – Classical Hodgkin Lymphoma",
      value: "CHL – Classical Hodgkin Lymphoma",
      therapies: [
        {
          label: "Metastatic",
          value: "Metastatic",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
      ],
    },
    {
      label: "mCRC – Metastatic Colorectal Cancer",
      value: "mCRC – Metastatic Colorectal Cancer",
      therapies: [
        {
          label: "Metastatic",
          value: "Metastatic",
          indications: [
            {
              label: "Single Agent",
              value: "Single Agent",
            },
          ],
        },
      ],
    },
  ],
  sigrima: [
    {
      label: "Breast Cancer",
      value: "Breast Cancer",
      therapies: [
        {
          label: "Neoadjuvant (before surgery)",
          value: "Neoadjuvant (before surgery)",
          indications: [],
        },
        {
          label: "Adjuvant (after surgery)",
          value: "Adjuvant (after surgery)",
          indications: [],
        },
        {
          label: "Metastatic 1st line",
          value: "Metastatic 1st line",
          indications: [],
        },
        {
          label: "Metastatic 2nd line & beyond",
          value: "Metastatic 2nd line & beyond",
          indications: [],
        },
      ],
    },
  ],
  tucatinib: [
    {
      label: "Breast Cancer",
      value: "Breast Cancer",
      therapies: [
        {
          label: "MBC",
          value: "MBC",
          indications: [],
        },
        {
          label: "MBC 1st line",
          value: "MBC 1st line",
          indications: [
            {
              label: "Tucatinib+ trastuzumab and capecitabine",
              value: "Tucatinib+ trastuzumab and capecitabine",
            },
            {
              label: "Tucatinib+trastuzumab",
              value: "Tucatinib+trastuzumab",
            },
          ],
        },
        {
          label: "MBC 2nd line",
          value: "MBC 2nd line",
          indications: [
            {
              label: "Tucatinib+ trastuzumab and capecitabine",
              value: "Tucatinib+ trastuzumab and capecitabine",
            },
            {
              label: "Tucatinib+trastuzumab",
              value: "Tucatinib+trastuzumab",
            },
          ],
        },
        {
          label: "MBC 3rd line",
          value: "MBC 3rd line",
          indications: [
            {
              label: "Tucatinib+ trastuzumab and capecitabine",
              value: "Tucatinib+ trastuzumab and capecitabine",
            },
            {
              label: "Tucatinib+trastuzumab",
              value: "Tucatinib+trastuzumab",
            },
          ],
        },
      ],
    },
  ],
};

// Chemotherapy Sessions Options
export const chemotherapySessionsV2 = [
  { label: "0", value: "0" },
  { label: "1 or more", value: "1" },
];

// Session Fields for Modal
export const sessionFieldsV2 = [
  {
    type: "text",
    label: "Cycle/Session Number",
    name: "cycle_number",
    placeholder: "Enter Cycle/Session Number",
    disabled: true,
  },
  {
    type: "date",
    label: "Upcoming cycle/session date",
    name: "upcoming_cycle_date",
    min: new Date().toISOString().split("T")[0],
    placeholder: "Select Upcoming Cycle/Session Date",
  },
  {
    type: "text",
    label: "Upcoming Dosage(mg)",
    name: "upcoming_dosage",
    placeholder: "Enter Upcoming Dosage(mg)",
    showForPrimaryDrug: ["nivolumab"], // Only show for Nivolumab
  },
  {
    type: "textarea",
    label: "Notes",
    name: "notes",
    placeholder: "Enter Notes",
  },
];

// Constants
export const DEFAULT_CARE_COINS = 250;
export const PRIMARY_DRUG_NIVOLUMAB = "nivolumab";
export const PRIMARY_DRUG_SIGRIMA = "sigrima";
export const PRIMARY_DRUG_TUCATINIB = "tucatinib";
export const SECONDARY_DRUG_VIVITRA = "vivitra";
